import styled, { keyframes } from 'styled-components';

import { resetButton } from 'styles/common';

const MARGIN = 20;
export const BTN_GAP = 60;

// ── Animations ───────────────────────────────────────────────────────────────

const orbPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.12); opacity: 1; }
`;

const ringRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const satelliteIn = keyframes`
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// ── Root ─────────────────────────────────────────────────────────────────────

export const Root = styled.div`
  position: fixed;
  bottom: ${MARGIN + 8}px;
  left: ${MARGIN}px;
  display: flex;
  flex-direction: column;
  gap: ${BTN_GAP}px;
  z-index: 101;
  pointer-events: none;
`;

// ── Slot for each button variant ────────────────────────────────────────────

export const ButtonSlot = styled.div<{ $size: number }>`
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  pointer-events: auto;
`;

// ── Button 1: Original main button ──────────────────────────────────────────

export const MainButton = styled.button<{ $size: number; $active: boolean }>`
  ${resetButton}
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  overflow: visible;
  transition: all 0.3s ease;

  background: ${(p) =>
    p.$active
      ? 'radial-gradient(circle at 30% 25%, rgba(0, 255, 120, 0.35), rgba(0, 120, 255, 0.15), rgba(0, 12, 30, 0.94))'
      : 'radial-gradient(circle at 35% 30%, rgba(0, 255, 120, 0.15), rgba(0, 17, 34, 0.9))'};

  border: 1.5px solid ${(p) => (p.$active ? 'rgba(0, 220, 160, 0.65)' : 'rgba(0, 255, 120, 0.25)')};

  box-shadow: ${(p) =>
    p.$active
      ? '0 0 16px rgba(0, 255, 120, 0.4), 0 0 32px rgba(0, 140, 255, 0.2), inset 0 0 12px rgba(0, 200, 180, 0.1)'
      : '0 0 10px rgba(0, 255, 120, 0.15)'};

  opacity: ${(p) => (p.$active ? 1 : 0.65)};

  svg {
    width: 38px;
    height: 38px;
    color: rgba(0, 255, 120);
    shape-rendering: geometricPrecision;
  }

  &:active {
    transform: scale(0.93);
  }

  @media (max-width: 768px) {
    svg {
      width: 34px;
      height: 34px;
    }
  }
`;

// ── Button 2: Space button ──────────────────────────────────────────────────

export const SpaceAuraCanvas = styled.canvas`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 170px;
  height: 170px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 0;
`;

export const SpaceButton = styled.button<{ $size: number; $active: boolean }>`
  ${resetButton}
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  overflow: visible;
  z-index: 5;
  transition: all 0.3s ease;

  background: ${(p) =>
    p.$active
      ? 'transparent'
      : 'radial-gradient(circle at 35% 30%, rgba(0, 255, 120, 0.15), rgba(0, 17, 34, 0.9))'};

  border: ${(p) => (p.$active ? 'none' : '1.5px solid rgba(0, 255, 120, 0.25)')};

  box-shadow: ${(p) =>
    p.$active
      ? '0 0 0 1px rgba(160,60,255,0.9), 0 0 18px rgba(140,40,255,1), 0 0 40px rgba(100,10,200,0.45), inset 0 1px 0 rgba(255,255,255,0.1)'
      : '0 0 10px rgba(0, 255, 120, 0.15)'};

  opacity: ${(p) => (p.$active ? 1 : 0.65)};

  svg {
    width: 26px;
    height: 26px;
    shape-rendering: geometricPrecision;
    transition:
      color 0.35s,
      filter 0.35s;
    color: ${(p) => (p.$active ? '#d8a8ff' : 'rgba(0, 255, 120)')};
    filter: ${(p) => (p.$active ? 'drop-shadow(0 0 8px rgba(180,80,255,1))' : 'none')};
  }

  &:hover {
    transform: scale(1.04);
  }

  &:active {
    transform: scale(0.93);
  }
`;

// ── Button 3: Neon button ───────────────────────────────────────────────────

export const NeonCanvasWrap = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 260px;
  height: 260px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 0;
`;

export const NeonCanvas = styled.canvas`
  display: block;
  width: 260px;
  height: 260px;
`;

export const NeonButtonEl = styled.button<{ $size: number; $active: boolean }>`
  ${resetButton}
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  overflow: visible;
  z-index: 5;
  transition: all 0.3s ease;

  background: ${(p) =>
    p.$active
      ? 'transparent'
      : 'radial-gradient(circle at 35% 30%, rgba(0, 255, 120, 0.15), rgba(0, 17, 34, 0.9))'};

  border: ${(p) => (p.$active ? 'none' : '1.5px solid rgba(0, 255, 120, 0.25)')};

  box-shadow: ${(p) => (p.$active ? 'none' : '0 0 10px rgba(0, 255, 120, 0.15)')};

  opacity: ${(p) => (p.$active ? 1 : 0.65)};

  svg {
    width: 26px;
    height: 26px;
    shape-rendering: geometricPrecision;
    position: relative;
    z-index: 2;
    transition:
      color 0.35s,
      filter 0.35s;
    color: ${(p) => (p.$active ? '#ffffff' : 'rgba(0, 255, 120)')};
    filter: ${(p) => (p.$active ? 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.7))' : 'none')};
  }

  &:active {
    transform: scale(0.93);
  }
`;

// ── Canvas for circular equalizer (button 1 active) ─────────────────────────

export const EqCanvas = styled.canvas<{ $size: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  pointer-events: none;
`;

// ── Orb (button 1 active) ───────────────────────────────────────────────────

export const OrbRing = styled.div`
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  border: 1.5px dashed rgba(0, 200, 180, 0.55);
  animation: ${ringRotate} 3s linear infinite;
  pointer-events: none;
`;

export const OrbRing2 = styled.div`
  position: absolute;
  inset: -1px;
  border-radius: 50%;
  border: 1px solid transparent;
  border-top-color: rgba(0, 140, 255, 0.4);
  border-right-color: rgba(0, 255, 120, 0.4);
  animation: ${ringRotate} 5s linear infinite reverse;
  pointer-events: none;
`;

export const OrbCore = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 18px;
  height: 18px;
  margin: -9px 0 0 -9px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 255, 160, 0.9) 0%, rgba(0, 120, 255, 0.45) 100%);
  box-shadow:
    0 0 10px rgba(0, 255, 140, 0.5),
    0 0 20px rgba(0, 140, 255, 0.2);
  animation: ${orbPulse} 1.5s ease-in-out infinite;
  pointer-events: none;
`;

// ── Satellite button (mic toggle) ────────────────────────────────────────────

export const SatelliteBtn = styled.button`
  ${resetButton}
  position: absolute;
  bottom: -4px;
  left: calc(100% + 3px);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  animation: ${satelliteIn} 0.25s ease-out forwards;
  -webkit-tap-highlight-color: transparent;
  background: radial-gradient(circle, rgba(0, 200, 255, 0.2), rgba(5, 10, 20, 0.92));
  border: 1px solid rgba(0, 200, 255, 0.35);
  box-shadow: 0 0 8px rgba(0, 200, 255, 0.2);
  color: rgba(0, 220, 255, 0.9);

  svg {
    width: 12px;
    height: 12px;

    path {
      fill: currentColor;
      stroke: currentColor;
    }
  }

  &:hover {
    transform: scale(1.15);
    filter: brightness(1.3);
  }

  &:active {
    transform: scale(0.9);
  }

  @media (max-width: 768px) {
    width: 22px;
    height: 22px;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

// ── Label ────────────────────────────────────────────────────────────────────

export const Label = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  font-size: 9px;
  font-weight: 700;
  color: rgba(200, 230, 255, 0.95);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  pointer-events: none;
  font-family: 'Rajdhani', sans-serif;
  white-space: nowrap;
`;
